import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  ExpandMore,
  Add,
  Delete,
  Save,
  DragIndicator,
} from '@mui/icons-material';
import { Checkbox as MuiCheckbox, FormControlLabel } from '@mui/material';
import { getActiveClearBoxConfig, updateClearBoxConfig } from '../../services/api';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

/* Shared input sx */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS,
    fontFamily: FONT_BODY,
    '&.Mui-focused fieldset': { borderColor: PRIMARY },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

export default function ClearBoxConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await getActiveClearBoxConfig();
        const data = res.data || res;
        const steps = typeof data.steps === 'string' ? JSON.parse(data.steps) : data.steps;
        setConfig({ ...data, steps: steps || [] });
      } catch (err) {
        setError(err.message || 'Failed to load config');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleTopLevelChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleStepChange = (stepIdx, field, value) => {
    setConfig((prev) => {
      const steps = [...prev.steps];
      steps[stepIdx] = { ...steps[stepIdx], [field]: value };
      return { ...prev, steps };
    });
  };

  const handleOptionChange = (stepIdx, optIdx, field, value) => {
    setConfig((prev) => {
      const steps = [...prev.steps];
      const options = [...(steps[stepIdx].options || [])];
      options[optIdx] = { ...options[optIdx], [field]: value };
      steps[stepIdx] = { ...steps[stepIdx], options };
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setConfig((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          id: `step-${Date.now()}`,
          title: '',
          description: '',
          placeholder: '',
          helpText: '',
          type: 'single-choice',
          hidden: false,
          section: '',
          options: [{ label: '', value: '' }],
        },
      ],
    }));
  };

  const removeStep = (idx) => {
    setConfig((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx),
    }));
  };

  const addOption = (stepIdx) => {
    setConfig((prev) => {
      const steps = [...prev.steps];
      steps[stepIdx] = {
        ...steps[stepIdx],
        options: [...(steps[stepIdx].options || []), { label: '', value: '' }],
      };
      return { ...prev, steps };
    });
  };

  const removeOption = (stepIdx, optIdx) => {
    setConfig((prev) => {
      const steps = [...prev.steps];
      steps[stepIdx] = {
        ...steps[stepIdx],
        options: steps[stepIdx].options.filter((_, i) => i !== optIdx),
      };
      return { ...prev, steps };
    });
  };

  /* ── Drag-and-drop reordering ──────────────────────────────── */
  const moveStep = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setConfig((prev) => {
      const steps = [...prev.steps];
      const [moved] = steps.splice(fromIdx, 1);
      steps.splice(toIdx, 0, moved);
      return { ...prev, steps };
    });
  };

  const handleDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragEnter = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx !== null && idx !== dragIdx) {
      setDragOverIdx(idx);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      moveStep(dragIdx, idx);
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateClearBoxConfig(config.id, {
        title: config.title,
        description: config.description,
        steps: config.steps,
        basePrice: config.basePrice,
        additionalLaserPrice: config.additionalLaserPrice,
      });
      setSuccess('Configuration saved successfully!');
    } catch (err) {
      setError(err.message || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!config) {
    return <Alert severity="warning" sx={{ borderRadius: RADIUS }}>No ClearBox configuration found.</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: FONT_HEADING,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${SECONDARY} 0%, ${PRIMARY} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            ClearBox Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', fontFamily: FONT_BODY }}>
            Edit the ClearBox questionnaire steps and options.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: RADIUS,
            textTransform: 'none',
            fontFamily: FONT_BODY,
            fontWeight: 600,
            px: 3,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 8px 20px rgba(108, 152, 173, 0.5)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>{error}</Alert>}

      {/* General fields */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: RADIUS,
          border: '2px solid rgba(108, 152, 173, 0.12)',
          boxShadow: CARD_SHADOW,
          transition: TRANSITION,
          '&:hover': { boxShadow: CARD_SHADOW_HOVER, borderColor: PRIMARY },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY, mb: 2 }}>
          General
        </Typography>
        <TextField
          fullWidth
          label="Title"
          value={config.title || ''}
          onChange={(e) => handleTopLevelChange('title', e.target.value)}
          sx={{ ...inputSx, mb: 2 }}
        />
        <TextField
          fullWidth
          label="Description"
          multiline
          minRows={2}
          value={config.description || ''}
          onChange={(e) => handleTopLevelChange('description', e.target.value)}
          sx={{ ...inputSx, mb: 2 }}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY, mb: 1.5 }}>
          Pricing
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Base Price ($)"
            type="number"
            value={config.basePrice ?? 9800}
            onChange={(e) => handleTopLevelChange('basePrice', parseFloat(e.target.value) || 0)}
            sx={{ ...inputSx, flex: 1 }}
            inputProps={{ min: 0, step: 100 }}
            helperText="Base ClearBox cost"
          />
          <TextField
            label="Additional Laser Price ($)"
            type="number"
            value={config.additionalLaserPrice ?? 4300}
            onChange={(e) => handleTopLevelChange('additionalLaserPrice', parseFloat(e.target.value) || 0)}
            sx={{ ...inputSx, flex: 1 }}
            inputProps={{ min: 0, step: 100 }}
            helperText="Cost per additional laser"
          />
        </Box>
        <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: '#888', fontFamily: FONT_BODY, fontStyle: 'italic' }}>
          *Prices exclude taxes
        </Typography>
      </Paper>

      {/* Steps header */}
      <Typography variant="subtitle1" sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY, mb: 1.5 }}>
        Steps ({config.steps.length})
      </Typography>

      {config.steps.map((step, sIdx) => (
        <Box
          key={step.id || sIdx}
          draggable
          onDragStart={handleDragStart(sIdx)}
          onDragEnter={handleDragEnter(sIdx)}
          onDragOver={handleDragOver}
          onDrop={handleDrop(sIdx)}
          onDragEnd={handleDragEnd}
          sx={{
            position: 'relative',
            opacity: dragIdx === sIdx ? 0.4 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {/* Drop position indicator */}
          {dragOverIdx === sIdx && dragIdx !== null && dragIdx !== sIdx && (
            <Box
              sx={{
                position: 'absolute',
                top: dragIdx > sIdx ? -2 : 'auto',
                bottom: dragIdx < sIdx ? -2 : 'auto',
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: PRIMARY,
                borderRadius: '2px',
                zIndex: 10,
                boxShadow: `0 0 8px ${PRIMARY}`,
              }}
            />
          )}
          <Accordion
            defaultExpanded={config.steps.length <= 3}
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: `${RADIUS} !important`,
              border: `2px solid ${dragOverIdx === sIdx && dragIdx !== null && dragIdx !== sIdx ? PRIMARY : 'rgba(108, 152, 173, 0.12)'}`,
              overflow: 'hidden',
              transition: TRANSITION,
              opacity: step.hidden ? 0.5 : 1,
              '&::before': { display: 'none' },
              '&:hover': { borderColor: 'rgba(108, 152, 173, 0.3)', opacity: 1 },
            }}
          >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: PRIMARY }} />}
            sx={{
              background: 'rgba(108, 152, 173, 0.04)',
              '&:hover': { background: 'rgba(108, 152, 173, 0.07)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <DragIndicator
                sx={{
                  color: '#ccc',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  '&:hover': { color: PRIMARY },
                  transition: TRANSITION,
                }}
                fontSize="small"
              />
              <Typography sx={{ flex: 1, fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY }}>
                {step.title || `Step ${sIdx + 1}`}
              </Typography>
              <Chip
                label={step.type || 'single-select'}
                size="small"
                sx={{
                  mr: 1,
                  fontFamily: FONT_BODY,
                  fontSize: '0.72rem',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(108, 152, 173, 0.1)',
                  color: PRIMARY,
                  fontWeight: 600,
                }}
              />
              {step.required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    mr: 1,
                    fontFamily: FONT_BODY,
                    fontSize: '0.72rem',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    color: '#d32f2f',
                    fontWeight: 600,
                  }}
                />
              )}
              {step.hidden && (
                <Chip
                  label="Hidden"
                  size="small"
                  sx={{
                    mr: 1,
                    fontFamily: FONT_BODY,
                    fontSize: '0.72rem',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                    color: '#ff9800',
                    fontWeight: 600,
                  }}
                />
              )}
              <Tooltip title="Remove step">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); removeStep(sIdx); }}
                  sx={{ color: '#ccc', transition: TRANSITION, '&:hover': { color: '#e53935' } }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-start' }}>
              <TextField
                label="Step Title"
                value={step.title || ''}
                onChange={(e) => handleStepChange(sIdx, 'title', e.target.value)}
                sx={{ ...inputSx, flex: 2, minWidth: 200 }}
              />
              <FormControl sx={{ flex: 1, minWidth: 140 }}>
                <InputLabel
                  sx={{ fontFamily: FONT_BODY, '&.Mui-focused': { color: PRIMARY } }}
                >
                  Type
                </InputLabel>
                <Select
                  value={step.type || 'single-select'}
                  onChange={(e) => handleStepChange(sIdx, 'type', e.target.value)}
                  label="Type"
                  sx={{
                    borderRadius: RADIUS,
                    fontFamily: FONT_BODY,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                  }}
                >
                  <MenuItem value="single-select">Single Choice</MenuItem>
                  <MenuItem value="multi-select">Multi Choice</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel
                  sx={{ fontFamily: FONT_BODY, '&.Mui-focused': { color: PRIMARY } }}
                >
                  Section
                </InputLabel>
                <Select
                  value={step.section || ''}
                  onChange={(e) => handleStepChange(sIdx, 'section', e.target.value)}
                  label="Section"
                  sx={{
                    borderRadius: RADIUS,
                    fontFamily: FONT_BODY,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                  }}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  <MenuItem value="machine-info">Machine Information</MenuItem>
                  <MenuItem value="scanner-config">Scanner Configuration</MenuItem>
                  <MenuItem value="monitoring-config">Monitoring Configuration</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <MuiCheckbox
                    checked={!!step.required}
                    onChange={(e) => handleStepChange(sIdx, 'required', e.target.checked)}
                    sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                  />
                }
                label="Required"
                sx={{
                  mt: 1,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: SECONDARY,
                  },
                }}
              />
              <FormControlLabel
                control={
                  <MuiCheckbox
                    checked={!!step.hidden}
                    onChange={(e) => handleStepChange(sIdx, 'hidden', e.target.checked)}
                    sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }}
                  />
                }
                label="Hidden"
                sx={{
                  mt: 1,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: SECONDARY,
                  },
                }}
              />
            </Box>

            {/* Parent requirement — field is hidden until the selected parent is answered */}
            <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
              <InputLabel
                sx={{ fontFamily: FONT_BODY, '&.Mui-focused': { color: PRIMARY } }}
              >
                Requires Field
              </InputLabel>
              <Select
                value={step.requiresParent || ''}
                onChange={(e) => handleStepChange(sIdx, 'requiresParent', e.target.value || '')}
                label="Requires Field"
                sx={{
                  borderRadius: RADIUS,
                  fontFamily: FONT_BODY,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                }}
              >
                <MenuItem value="">
                  <em>None (always visible)</em>
                </MenuItem>
                {config.steps
                  .filter((_, i) => i !== sIdx)
                  .map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.title || s.id}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description"
              multiline
              minRows={1}
              value={step.description || ''}
              onChange={(e) => handleStepChange(sIdx, 'description', e.target.value)}
              sx={{ ...inputSx, mb: 2 }}
            />

            <TextField
              fullWidth
              label="Placeholder"
              value={step.placeholder || ''}
              onChange={(e) => handleStepChange(sIdx, 'placeholder', e.target.value)}
              sx={{ ...inputSx, mb: 2 }}
              helperText="Placeholder text shown in input fields (for text type steps)"
            />

            <TextField
              fullWidth
              label="Help Text"
              value={step.helpText || ''}
              onChange={(e) => handleStepChange(sIdx, 'helpText', e.target.value)}
              sx={{ ...inputSx, mb: 2 }}
              helperText="Information message shown below the field"
            />

            <Divider sx={{ my: 2, borderColor: 'rgba(108, 152, 173, 0.1)' }} />
            <Typography variant="body2" sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY, mb: 1.5 }}>
              Options ({(step.options || []).length})
            </Typography>

            {(step.options || []).map((opt, oIdx) => (
              <Box key={oIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label="Label"
                  value={opt.label || ''}
                  onChange={(e) => handleOptionChange(sIdx, oIdx, 'label', e.target.value)}
                  sx={{ ...inputSx, flex: 2 }}
                />
                <TextField
                  size="small"
                  label="Value"
                  value={opt.value || ''}
                  onChange={(e) => handleOptionChange(sIdx, oIdx, 'value', e.target.value)}
                  sx={{ ...inputSx, flex: 1 }}
                />
                <Tooltip title="Remove option">
                  <IconButton
                    size="small"
                    onClick={() => removeOption(sIdx, oIdx)}
                    sx={{ color: '#ccc', transition: TRANSITION, '&:hover': { color: '#e53935' } }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}

            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => addOption(sIdx)}
              sx={{
                mt: 1,
                borderRadius: RADIUS,
                textTransform: 'none',
                fontFamily: FONT_BODY,
                color: PRIMARY,
                '&:hover': { backgroundColor: 'rgba(108, 152, 173, 0.08)' },
              }}
            >
              Add Option
            </Button>
          </AccordionDetails>
          </Accordion>
        </Box>
      ))}

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={addStep}
        fullWidth
        sx={{
          mt: 2,
          borderRadius: RADIUS,
          textTransform: 'none',
          fontFamily: FONT_BODY,
          fontWeight: 600,
          py: 1.5,
          borderColor: 'rgba(108, 152, 173, 0.3)',
          color: PRIMARY,
          transition: TRANSITION,
          '&:hover': {
            borderColor: PRIMARY,
            backgroundColor: 'rgba(108, 152, 173, 0.06)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        Add Step
      </Button>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        message={success}
        ContentProps={{
          sx: {
            borderRadius: RADIUS,
            fontFamily: FONT_BODY,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
          },
        }}
      />
    </Box>
  );
}
